import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Mail, Lock, GraduationCap, Loader2, ArrowLeft, ShieldCheck } from "lucide-react";
import { useBranding } from "@/context/BrandingContext";
import { useAuth } from "@/context/AuthContext";
import { authService } from "@/services/authService";
import { parseThrottleSeconds } from "@/utils/helpers";

type Step = "email" | "verify" | "password";

const AgencyForgotPassword = () => {
    const { branding, loading } = useBranding();
    const { setUser } = useAuth();
    const navigate = useNavigate();

    const primaryColor = branding?.branding?.colors?.primary || "#1d3658";

    const [step, setStep] = useState<Step>("email");
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);

    const [isResending, setIsResending] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [resendSuccess, setResendSuccess] = useState(false);
    const [throttleCooldown, setThrottleCooldown] = useState(0);

    useEffect(() => {
        if (resendCooldown <= 0) return;
        const timer = setInterval(() => {
            setResendCooldown((prev) => Math.max(0, prev - 1));
        }, 1000);
        return () => clearInterval(timer);
    }, [resendCooldown]);

    useEffect(() => {
        if (throttleCooldown <= 0) return;
        const timer = window.setTimeout(() => {
            setThrottleCooldown((prev) => Math.max(0, prev - 1));
        }, 1000);
        return () => window.clearTimeout(timer);
    }, [throttleCooldown]);

    const setError = (msg: string | string[]) =>
        setErrors(Array.isArray(msg) ? msg : [msg]);
    const clearErrors = () => setErrors([]);

    const formatCooldown = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    const handleEmailSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        clearErrors();
        setIsSubmitting(true);
        try {
            const data = await authService.agencyRequestPasswordReset(email);
            setResendCooldown(data?.expires_in || 900);
            setStep("verify");
        } catch (err: any) {
            setError(
                err.response?.data?.detail ||
                err.response?.data?.error ||
                "Something went wrong. Please try again."
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCodeSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        clearErrors();
        if (!code.trim()) {
            setError("Please enter the verification code.");
            return;
        }
        setIsSubmitting(true);
        try {
            await authService.agencyVerifyResetCode(email, code);
            setStep("password");
        } catch (err: any) {
            if (err.response?.status === 429) {
                clearErrors();
                setThrottleCooldown(parseThrottleSeconds(err.response?.data?.detail));
            } else {
                setError(
                    err.response?.data?.message ||
                    err.response?.data?.detail ||
                    err.response?.data?.error ||
                    "Invalid or expired verification code. Please try again."
                );
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        clearErrors();
        if (newPassword !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }
        setIsSubmitting(true);
        try {
            const data = await authService.agencyResetPassword(email, code, newPassword);
            if (data.user) {
                setUser(data.user);
            }
            navigate("/partner");
        } catch (err: any) {
            const errorField = err.response?.data?.new_password;
            if (errorField) {
                setError(Array.isArray(errorField) ? errorField : [errorField]);
            } else {
                setError(
                    err.response?.data?.detail ||
                    err.response?.data?.error ||
                    "Something went wrong. Please try again."
                );
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResendCode = async () => {
        if (resendCooldown > 0 || isResending) return;
        clearErrors();
        setResendSuccess(false);
        setIsResending(true);
        try {
            const data = await authService.agencyRequestPasswordReset(email);
            setResendSuccess(true);
            setResendCooldown(data?.expires_in || 900);
            setCode("");
        } catch {
            setError("Could not resend code. Please try again.");
        } finally {
            setIsResending(false);
        }
    };

    const Logo = () => (
        <Link to="/" className="flex flex-col items-center gap-3 mb-4 group text-center">
            <div className="w-20 h-20 flex items-center justify-center">
                {!loading && branding?.branding?.logo ? (
                    <img
                        src={branding.branding.logo}
                        alt={`${branding.name} Logo`}
                        className="max-w-full max-h-full object-contain"
                    />
                ) : (
                    <div
                        className="w-full h-full rounded-lg flex items-center justify-center text-white"
                        style={{ backgroundColor: primaryColor }}
                    >
                        <GraduationCap className="w-12 h-12" />
                    </div>
                )}
            </div>
            <span
                className="font-serif text-3xl font-black text-primary text-center"
                style={{ fontFamily: "var(--font-university-name)", color: primaryColor }}
            >
                {!loading && branding?.name ? branding.name : "University Portal"}
            </span>
        </Link>
    );

    const ErrorBox = () =>
        errors.length > 0 ? (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg space-y-1">
                {errors.map((msg, idx) => (
                    <p key={idx}>{msg}</p>
                ))}
            </div>
        ) : null;

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50/50 p-4 font-sans text-foreground">
            <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-3xl" />
                <div className="absolute -bottom-[10%] -right-[10%] w-[40%] h-[40%] bg-secondary/10 rounded-full blur-3xl" />
            </div>

            <div className="w-full max-w-md relative z-10">
                <div className="flex flex-col items-center mb-8">
                    <Logo />
                    <h1 className="text-xl font-bold text-slate-900">Reset Partner Password</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {step === "email" && "Enter your agency email to receive a verification code."}
                        {step === "verify" && "Enter the verification code we sent."}
                        {step === "password" && "Choose a new password for your agency account."}
                    </p>
                </div>

                {/* Step indicator */}
                <div className="flex items-center justify-center gap-2 mb-6">
                    {(["email", "verify", "password"] as Step[]).map((s, i) => (
                        <div key={s} className="flex items-center gap-2">
                            <div
                                className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all"
                                style={
                                    step === s
                                        ? { backgroundColor: primaryColor, color: "#fff" }
                                        : ["email", "verify", "password"].indexOf(step) > ["email", "verify", "password"].indexOf(s)
                                        ? { backgroundColor: `${primaryColor}30`, color: primaryColor }
                                        : { backgroundColor: "#e2e8f0", color: "#94a3b8" }
                                }
                            >
                                {i + 1}
                            </div>
                            {i < 2 && (
                                <div
                                    className="w-8 h-0.5 rounded"
                                    style={{
                                        backgroundColor:
                                            ["email", "verify", "password"].indexOf(step) > i
                                                ? primaryColor
                                                : "#e2e8f0",
                                    }}
                                />
                            )}
                        </div>
                    ))}
                </div>

                <Card className="border-none shadow-2xl overflow-hidden bg-white/80 backdrop-blur-sm">
                    <CardContent className="p-8">
                        {/* Step 1: Email */}
                        {step === "email" && (
                            <form onSubmit={handleEmailSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="afp-email" className="text-slate-700">
                                        Email Address
                                    </Label>
                                    <div className="relative">
                                        <Mail
                                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                            size={18}
                                        />
                                        <Input
                                            id="afp-email"
                                            type="email"
                                            placeholder="your.email@example.com"
                                            className="h-11 pl-10 border-slate-200 focus-visible:ring-1"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            autoFocus
                                        />
                                    </div>
                                </div>

                                <ErrorBox />

                                <Button
                                    type="submit"
                                    className="w-full h-11 text-white hover:opacity-90 transition-opacity"
                                    style={{ backgroundColor: primaryColor }}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Sending code…
                                        </>
                                    ) : (
                                        "Send Verification Code"
                                    )}
                                </Button>

                                <div className="text-center">
                                    <Link
                                        to="/partner/agency-login"
                                        className="text-sm inline-flex items-center gap-1 text-slate-500 hover:text-slate-700"
                                    >
                                        <ArrowLeft size={14} />
                                        Back to Partner Login
                                    </Link>
                                </div>
                            </form>
                        )}

                        {/* Step 2: Verification code */}
                        {step === "verify" && (
                            <form onSubmit={handleCodeSubmit} className="space-y-4">
                                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-700">
                                    If <span className="font-semibold">{email}</span> is registered,
                                    a verification code has been sent to that address. Enter it below.
                                </div>

                                {resendSuccess && (
                                    <div className="bg-green-50 border border-green-100 rounded-lg p-3 text-sm text-green-700">
                                        Verification code resent successfully!
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <Label htmlFor="afp-code" className="text-slate-700">
                                        Verification Code
                                    </Label>
                                    <div className="relative">
                                        <ShieldCheck
                                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                            size={18}
                                        />
                                        <Input
                                            id="afp-code"
                                            type="text"
                                            placeholder="Enter 6-character code"
                                            className="h-11 pl-10 border-slate-200 focus-visible:ring-1 tracking-widest text-center text-lg font-mono uppercase placeholder:text-xs placeholder:tracking-normal placeholder:font-sans placeholder:normal-case"
                                            value={code}
                                            onChange={(e) => {
                                                setCode(e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase());
                                                setResendSuccess(false);
                                            }}
                                            maxLength={6}
                                            required
                                            autoFocus
                                        />
                                    </div>
                                </div>

                                {throttleCooldown > 0 && (
                                    <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-sm text-amber-700">
                                        Too many attempts. Please wait {formatCooldown(throttleCooldown)} before trying again.
                                    </div>
                                )}

                                <ErrorBox />

                                <Button
                                    type="submit"
                                    className="w-full h-11 text-white hover:opacity-90 transition-opacity"
                                    style={{ backgroundColor: primaryColor }}
                                    disabled={isSubmitting || throttleCooldown > 0}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Verifying…
                                        </>
                                    ) : (
                                        "Verify Code"
                                    )}
                                </Button>

                                <div className="flex items-center justify-between text-sm text-slate-500">
                                    <button
                                        type="button"
                                        onClick={handleResendCode}
                                        disabled={resendCooldown > 0 || isResending}
                                        className="hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isResending
                                            ? "Resending…"
                                            : resendCooldown > 0
                                            ? `Resend in ${formatCooldown(resendCooldown)}`
                                            : "Resend code"}
                                    </button>
                                    <Link
                                        to="/partner/agency-login"
                                        className="inline-flex items-center gap-1 hover:text-slate-700"
                                    >
                                        <ArrowLeft size={14} />
                                        Back to login
                                    </Link>
                                </div>
                            </form>
                        )}

                        {/* Step 3: New password */}
                        {step === "password" && (
                            <form onSubmit={handlePasswordSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="afp-new-password" className="text-slate-700">
                                        New Password
                                    </Label>
                                    <div className="relative">
                                        <Lock
                                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                            size={18}
                                        />
                                        <Input
                                            id="afp-new-password"
                                            type="password"
                                            placeholder="Enter new password"
                                            className="h-11 pl-10 border-slate-200 focus-visible:ring-1"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            required
                                            autoFocus
                                            minLength={8}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="afp-confirm-password" className="text-slate-700">
                                        Confirm Password
                                    </Label>
                                    <div className="relative">
                                        <Lock
                                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                            size={18}
                                        />
                                        <Input
                                            id="afp-confirm-password"
                                            type="password"
                                            placeholder="Confirm new password"
                                            className="h-11 pl-10 border-slate-200 focus-visible:ring-1"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                            minLength={8}
                                        />
                                    </div>
                                </div>

                                <ErrorBox />

                                <Button
                                    type="submit"
                                    className="w-full h-11 text-white hover:opacity-90 transition-opacity"
                                    style={{ backgroundColor: primaryColor }}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Saving…
                                        </>
                                    ) : (
                                        "Set New Password"
                                    )}
                                </Button>
                            </form>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default AgencyForgotPassword;
